import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../services/api';
import Card from '../ui/Card';
import { Notice, tabStyle, apiErrors } from '../components/insurance/formBits';
import ClaimStatusBadge from '../components/claims/ClaimStatusBadge';
import { formatLongDate } from '../dateOnly';
import { formatMoney } from '../insurance/format';

// My Claims (therapist): every claim where I am the rendering provider. The one
// thing only I can do is attest the progress note; the rest lives on the detail page.
const ATTEST_TEXT = 'Progress note is complete and signed for this session.';
const FILTERS = [
  ['open', 'Open', 'draft,ready,submitted,accepted,pending,rejected'],
  ['closed', 'Closed', 'paid,partially_paid,denied,corrected,voided'],
  ['all', 'All', ''],
];
const th = { padding: '8px 10px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 13 };
const td = { padding: '8px 10px', verticalAlign: 'top', fontSize: 13 };

export default function TherapistClaimsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('open');
  const [data, setData] = useState({ items: [], showAmounts: false });
  const [msg, setMsg] = useState(null);
  const [attesting, setAttesting] = useState(() => new Set());   // claim ids with an attest request in flight
  useEffect(() => { if (!localStorage.getItem('token')) navigate('/'); }, [navigate]);

  const load = useCallback(async () => {
    const status = FILTERS.find((f) => f[0] === filter)[2];
    try { setData((await API.get('/billing/claims', { params: status ? { status, pageSize: 200 } : { pageSize: 200 } })).data); }
    catch (err) { setMsg({ tone: 'bad', text: apiErrors(err).message }); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  async function attest(id) {
    if (attesting.has(id)) return;
    setAttesting((s) => new Set(s).add(id));
    setMsg(null);
    try {
      await API.post(`/billing/claims/${id}/attest`);
      setMsg({ tone: 'good', text: 'Attestation recorded. The office can submit once every check passes.' });
      await load();
    } catch (err) { setMsg({ tone: 'bad', text: apiErrors(err).message }); }
    finally { setAttesting((s) => { const n = new Set(s); n.delete(id); return n; }); }
  }

  const columns = ['Client', 'Date of service', 'CPT', 'Payer', data.showAmounts ? 'Charge' : null, 'Status', 'What is next'].filter(Boolean);
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px' }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary-color)' }}>My Claims</h1>
      <p style={{ margin: '4px 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
        Insurance claims for your sessions. Attest each progress note so the office can send the claim.
      </p>
      {msg && <div style={{ marginBottom: 12 }}><Notice tone={msg.tone}>{msg.text}</Notice></div>}
      <Card>
        <div style={{ borderBottom: '1px solid var(--border-color)', marginBottom: 10 }}>
          {FILTERS.map(([k, label]) => <button key={k} type="button" style={tabStyle(filter === k)} onClick={() => setFilter(k)}>{label}</button>)}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--secondary-color)' }}>{columns.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.items.length === 0 && <tr><td colSpan={columns.length} style={{ ...td, color: 'var(--text-muted)' }}>No claims here.</td></tr>}
              {data.items.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                  <td style={td}><Link to={`/therapist/claims/${c.id}`}>{c.client_name}</Link></td>
                  <td style={td}>{formatLongDate(c.dos)}</td>
                  <td style={td}>{c.cpt}</td>
                  <td style={td}>{c.payer_name}</td>
                  {data.showAmounts && <td style={td}>{c.total_charge === null || c.total_charge === undefined ? '' : formatMoney(c.total_charge)}</td>}
                  <td style={td}><ClaimStatusBadge status={c.status} statusLabel={c.status_label} /></td>
                  <td style={{ ...td, maxWidth: 360 }}>
                    {['draft', 'ready', 'rejected'].includes(c.status) && !c.note_attested_at
                      ? (
                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
                          <input type="checkbox" aria-label={ATTEST_TEXT} checked={attesting.has(c.id)} disabled={attesting.has(c.id)} onChange={() => attest(c.id)} />
                          <span>{ATTEST_TEXT}</span>
                        </label>
                      )
                      : <span>{c.next_action}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.total > data.items.length && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>Showing the latest {data.items.length} of {data.total} claims.</div>
        )}
      </Card>
    </div>
  );
}
